import { createContext, useMemo, useEffect, useState, useRef } from 'react';
import debounce from 'lodash/debounce';

import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';
import useQuery from '@/utilities/useQuery';
import { useClientUser, useReturnOrderFlow } from '@/hooks';
import { useNavigate } from 'react-router';
import {
    GET_EXCEPTIONS,
    RESOLVE_EXCEPTION,
    GET_CARRIERS,
    GET_SHIPPERS,
} from '@/components/admin/AdminExceptions/graphql';
import { useAdminExceptionHooks } from '@/components/admin/AdminExceptions/hooks';
import { createOrderJob } from '@/components/ShipmentForm/queries/createOrderJob';
import { exportCsv } from '@/components/admin/AdminExceptions/queries/exportCsv';
import { useLazyQuery } from '@apollo/client';
import { captureException } from '@sentry/react';

import { useColumns, FILTERABLE_COLUMN_IDS } from './columns';
import { carrierDisplayName } from '@/utilities/carrierAlias';
import { UPDATE_ORDER_BY_ID } from '@/graphql/mutations/orders';

const DEFAULT_SORT_BY = { id: 'exception_time', desc: true };

export const Context = createContext();

export const ContextProvider = ({ children }) => {
    const navigate = useNavigate();
    const { user_id, partnerCarriersById } = useClientUser();
    const columns = useColumns();
    const [toResolve, setToResolve] = useState({});
    const [selected, setSelected] = useState({});
    const [hasMore, setHasMore] = useState(false);
    const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
    const [filter, setFilter] = useState({
        status: 'OPEN',
        shippers: [],
        carriers: [],
        searchTerm: '',
    });
    const skipNextFilterRefetch = useRef(true);

    const [getExceptions, { data, loading: initInflight, fetchMore }] = useLazyQuery(GET_EXCEPTIONS, {
        onError: (err) => {
            console.error(err);
            captureException(err);
        },
    });

    const [getShippers, { data: shipperData }] = useLazyQuery(GET_SHIPPERS, {
        onError: (err) => {
            captureException(err);
        },
    });

    const [getCarriers, { data: carrierData }] = useLazyQuery(GET_CARRIERS, {
        onError: (err) => {
            captureException(err);
        },
    });

    const [updateOrder] = useMutation(UPDATE_ORDER_BY_ID, {
        onError: (err) => {
            captureException(err);
        },
    });

    const exceptions = useMemo(() => {
        return data?.results || [];
    }, [data]);

    const shippers = useMemo(() => {
        return (
            (shipperData?.results || [])
                .filter((order) => !!order.shipper_id)
                .map((order) => {
                    return {
                        label: order?.order_shipper?.business_name,
                        value: order.shipper_id,
                    };
                })
                .sort((l, r) => l.label.localeCompare(r.label)) || []
        );
    }, [shipperData]);

    const carriers = useMemo(() => {
        return (
            (carrierData?.results || [])
                .filter((order) => !!order.carrier_id)
                .map((order) => {
                    return {
                        // Aliased alongside the Carrier column; the value stays the real
                        // carrier_id so the filter query is unchanged.
                        label: carrierDisplayName(order?.order_carrier, partnerCarriersById),
                        value: order.carrier_id,
                    };
                }) || []
        );
    }, [carrierData, partnerCarriersById]);

    const where = useMemo(() => {
        const clause = [
            {
                _or: [
                    { order: { shipper_id: { _eq: user_id } } },
                    { order: { carrier_id: { _eq: user_id } } },
                    { order: { warehouse_carrier_id: { _eq: user_id } } },
                ],
            },
            ...(filter.start ? [{ created_at: { _gte: filter.start.toISOString() } }] : []),
            ...(filter.end ? [{ created_at: { _lt: filter.end.toISOString() } }] : []),
            ...(filter?.shippers?.length > 0 ? [{ order: { shipper_id: { _in: filter?.shippers } } }] : []),
            ...(filter?.carriers?.length > 0 ? [{ order: { carrier_id: { _in: filter?.carriers } } }] : []),
            ...(filter.type ? [{ exception: { type: { _eq: filter.type } } }] : []),
            ...(filter.occurred_at ? [{ exception: { reported_at: { _eq: filter.occurred_at } } }] : []),
            ...(filter?.searchTerm?.length
                ? [
                      {
                          _or: [
                              { order: { order_number: { _ilike: `%${filter?.searchTerm}%` } } },
                              { order: { po_number: { _ilike: `%${filter?.searchTerm}%` } } },
                          ],
                      },
                  ]
                : []),
        ];
        return clause;
    }, [filter, user_id]);

    const filtered = useMemo(() => {
        const result = Object.values(
            exceptions
                .filter((exception) => {
                    const resolved = exception.exception.status === 'RESOLVED';
                    return filter.status === 'OPEN' ? !resolved : resolved;
                })
                .reduce((acc, exception) => {
                    acc[exception.exception.exception_id] = {
                        ...exception,
                        ...(exception.item
                            ? { item: [...((acc[exception.exception.exception_id] || {}).item || []), exception.item] }
                            : {}),
                    };

                    return acc;
                }, {})
        );
        return result;
    }, [exceptions, filter]);

    const order_by = useMemo(() => {
        const ascOrDesc = sortBy?.desc ? 'desc_nulls_last' : 'asc_nulls_last';
        return [{ created_at: ascOrDesc }, { exception_type: 'desc_nulls_last' }];
    }, [sortBy]);

    const [retrieveCsv, { loading: csvInflight }] = useQuery(exportCsv, {
        onError: (err) => {
            captureException(err);
        },
    });

    const { exportCSV, loadMore } = useAdminExceptionHooks(
        { where, exceptions, order_by },
        { fetchMore, setHasMore, retrieveCsv }
    );

    const getExceptionsDebounced = useMemo(
        () =>
            debounce((payload) => {
                return getExceptions(payload);
            }, 500),
        []
    );

    useEffect(() => {
        return () => {
            getExceptionsDebounced.cancel();
        };
    }, [getExceptionsDebounced]);

    useEffect(() => {
        if (!user_id) return;

        skipNextFilterRefetch.current = true;
        setHasMore(true);
        getExceptions({
            variables: {
                where: { _and: where },
                order_by,
            },
        });

        getShippers({
            variables: {
                where: {
                    _and: [
                        {
                            _or: [{ shipper_id: { _eq: user_id } }, { carrier_id: { _eq: user_id } }],
                        },
                        ...(filter.start ? [{ created_at: { _gte: filter.start.toISOString() } }] : []),
                        ...(filter.end ? [{ created_at: { _lt: filter.end.toISOString() } }] : []),
                    ],
                },
            },
        });

        getCarriers({
            variables: {
                where: {
                    _and: [
                        {
                            _or: [{ shipper_id: { _eq: user_id } }, { carrier_id: { _eq: user_id } }],
                        },
                        ...(filter.start ? [{ created_at: { _gte: filter.start.toISOString() } }] : []),
                        ...(filter.end ? [{ created_at: { _lt: filter.end.toISOString() } }] : []),
                    ],
                },
            },
        });
    }, [user_id]);

    useEffect(() => {
        if (!user_id) return;
        if (skipNextFilterRefetch.current) {
            skipNextFilterRefetch.current = false;
            return;
        }

        getExceptionsDebounced({
            variables: {
                where: { _and: where },
                order_by,
            },
        });
        setHasMore(true);
    }, [user_id, where, order_by, getExceptionsDebounced]);

    const [submitOrders, { loading: submitInflight }] = useQuery(createOrderJob, {
        onError: (err) => {
            Sentry.captureException(err);
        },
    });

    const [resolve, { loading: resolveInflight }] = useMutation(RESOLVE_EXCEPTION, {
        update(cache, { data: { order, exception, items_removed } }) {
            cache.updateQuery(
                {
                    query: GET_EXCEPTIONS,
                    variables: {
                        where: { _and: where },
                        order_by,
                    },
                },
                (data) => {
                    const removed = Object.fromEntries(items_removed.returning.map((item) => [item.item_id, true]));
                    const [resolved] = exception.returning;
                    const [updated] = order.returning;

                    const clone = [...data.results].map((mapping) => {
                        return {
                            ...mapping,
                            ...(mapping.exception.exception_id === resolved.exception_id
                                ? {
                                      order: updated ? updated : mapping.order,
                                      exception: {
                                          ...mapping.exception,
                                          status: resolved.status,
                                      },
                                  }
                                : {}),
                        };
                    });

                    return { results: clone.filter((item) => !removed[item.item_id]) };
                }
            );
        },
        onError: (err) => {
            Sentry.captureException(err);
        },
    });

    const saveAssociatedReturnOrder = useReturnOrderFlow({
        setReturnOrderId: (oid, rid) => updateOrder({ variables: { order_id: oid, update: { return_order_id: rid } } }),
    });

    const callbacks = {
        resolveException: (row) => {
            const mapping = row?.original ?? row;
            setToResolve({
                exception: mapping.exception,
                items: mapping.item,
                order: mapping.order,
            });
        },
        closeModal: () => {
            setToResolve({});
        },
        selectExceptions: setSelected,
        exportCSV,
        setFilter,
        setSortBy,
        loadMore,
        submitOrders,
        resolve,
        saveAssociatedReturnOrder,
        gotoDetails: (order) => navigate(`/order/${order?.order_id}`),
        user_id,
    };

    return (
        <Context.Provider
            value={{
                state: {
                    toResolve,
                    filter,
                    hasMore,
                    selected,
                    sortBy,
                    exceptions: filtered,
                    columns,
                    filterableColumnIds: FILTERABLE_COLUMN_IDS,
                    shippers,
                    carriers,
                    tableType: 'EXCEPTION_MANAGER',
                },
                loading: {
                    init: initInflight,
                    resolve: resolveInflight || submitInflight,
                    export: csvInflight,
                },
                callbacks,
            }}
        >
            {children}
        </Context.Provider>
    );
};
